// =============================================================
// TaskProposalsModal.jsx — the action items Harvi found, put in front of you
// WHEREVER YOU ARE in the app.
//
// A panel on a deal's Notes & Tasks tab only reaches somebody already looking
// at that deal, which is precisely not the case this feature exists for: an
// email captured onto a deal lands with nobody watching. So the proposals are
// pushed — one modal, mounted globally, listing everything still awaiting a
// decision across every deal the reader can see.
//
// NOTHING IS CREATED UNTIL "Add". Each add posts /api/tasks under the reader's
// own token, so the Admin gate, the audit, the notifications and the
// near-duplicate gate all apply. THE DUPLICATE GATE IS NOT BYPASSED BY "Add
// all": a proposal that looks like a task you already have is LEFT IN THE LIST
// flagged, for a decision on its own, rather than quietly creating a second
// copy — which is exactly what a reader who pressed one button would not expect,
// and the whole reason an unprompted reader needs that gate.
//
// IT MUST NOT NAG. The pop-up opens only when there is something the reader has
// not been shown before (`seen` keys in localStorage, so a reload is not a
// second interruption); "Later" marks everything currently listed as seen and
// closes. Whatever is still outstanding comes back with the next new proposal —
// the list is always everything open, never only the new ones.
// =============================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { Btn, Modal, ModalFooter, Icon, useToast } from '../ui';
import { TaskDraftModal } from './TaskDraftModal';
import { formatInstant } from '../../utils/format';
import { notifyTasksChanged } from '../../utils/taskEvents';

const SEEN_KEY = 'harvus.taskProposals.seen';
const SEEN_CAP = 400;                 // enough to remember; bounded so it cannot grow forever
const POLL_MS  = 5 * 60 * 1000;       // the reader is not waiting on this — a note takes seconds to read, not milliseconds
const FIRST_CHECK_MS = 4000;          // let the page paint before interrupting it

const itemKey = (g, it) => `${g.suggestionId}:${it.index}`;

// localStorage is a per-viewer convenience here and every accessor can throw
// (private windows, blocked site data), so every read and write is guarded and
// the component renders correctly with no stored value.
const readSeen = () => {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch { return new Set(); }
};
const writeSeen = (set) => {
  try {
    window.localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-SEEN_CAP)));
  } catch { /* a viewer who cannot store this simply gets shown it again */ }
};

const fmtWhen = (utc) => {
  if (!utc) return '';
  return formatInstant(utc, { month: 'short', day: 'numeric', year: 'numeric' });
};

// The deal a proposal belongs to, as one line. A detail whose description just
// repeats the deal name says nothing twice (createDealSvc seeds the first
// detail that way), so it is dropped.
const scopeLabel = (g) => {
  const deal = g.dealName || 'Unknown deal';
  const detail = g.detailName && g.detailName !== g.dealName ? ` · ${g.detailName}` : '';
  return `${deal}${detail}`;
};

// ---------------------------------------------------------------------------
// The modal itself. `groups` is what is still open; every action reports back
// through the callbacks so the owner can keep the list and the server in step.
// ---------------------------------------------------------------------------
export const TaskProposalsModal = ({
  groups, allowTasks = true, truncated = false, addingAll,
  duplicates = {}, expanded, noteBody, noteLoading,
  onToggleNote, onAdd, onAddAnyway, onDismiss, onAddAll, onDismissAll, onClose,
}) => {
  const total = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <Modal
      title={`Harvi found ${total} possible task${total === 1 ? '' : 's'} in recent notes`}
      width={680}
      onClose={onClose}
    >
      <style>{`
        .tp-note h1, .tp-note h2, .tp-note h3 { font-size: 13px; font-weight: 700; color: ${B.navyDk}; margin: 10px 0 2px; }
        .tp-note p { margin: 5px 0; }
        .tp-note ul, .tp-note ol { margin: 4px 0; padding-left: 20px; }
        .tp-note a { color: #2563eb; text-decoration: underline; }
        .tp-note table { border-collapse: collapse; }
        .tp-note td, .tp-note th { border: 1px solid ${B.border}; padding: 2px 6px; }
      `}</style>

      <div style={{ fontSize: 12, color: B.textMd, marginBottom: 12, lineHeight: 1.5 }}>
        These came out of notes you added — including emails you filed onto a deal. Proposals from
        a colleague's notes stay on that deal's Notes &amp; Tasks tab.
        <strong> Nothing is created until you add it.</strong>
      </div>

      <div style={{ maxHeight: '52vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {groups.map(g => {
          const isOpen = expanded === g.suggestionId;
          return (
            <div key={g.suggestionId} style={{ border: `1px solid ${B.border}`, borderRadius: 8, background: B.white }}>
              {/* Where it came from — click to read the note itself. */}
              <button
                onClick={() => onToggleNote(g)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', textAlign: 'left',
                  padding: '9px 11px', border: 'none', background: B.surfaceMuted, cursor: 'pointer',
                  borderRadius: isOpen ? '8px 8px 0 0' : 8, fontFamily: 'inherit',
                }}
                aria-expanded={isOpen}
              >
                <Icon name="chevronDown" size={13} style={{ marginTop: 2, flexShrink: 0, transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: B.navyDk, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {scopeLabel(g)}
                  </span>
                  <span style={{ display: 'block', fontSize: 11, color: B.textMd, marginTop: 1 }}>
                    {g.sourceType === 'email'
                      ? <>✉️ From an email{g.emailSubject ? ` · ${g.emailSubject}` : ''}</>
                      : <>Note by {g.noteAuthor || 'someone'}</>}
                    {g.noteDate ? ` · ${fmtWhen(g.noteDate)}` : ''}
                  </span>
                </span>
                <span style={{ fontSize: 11, color: B.textLt, whiteSpace: 'nowrap', marginTop: 2 }}>
                  {isOpen ? 'Hide note' : 'Read note'}
                </span>
              </button>

              {isOpen && (
                <div style={{ padding: '10px 12px', borderBottom: `1px solid ${B.border}`, background: B.cream || B.surfaceMuted, maxHeight: 240, overflowY: 'auto' }}>
                  {noteLoading ? (
                    <div style={{ fontSize: 12, color: B.textLt, fontStyle: 'italic' }}>Loading the note…</div>
                  ) : noteBody?.isHtml ? (
                    <div className="tp-note" style={{ fontSize: 12.5, lineHeight: 1.6, color: B.text, wordBreak: 'break-word' }}
                      dangerouslySetInnerHTML={{ __html: noteBody.content }} />
                  ) : (
                    <div style={{ fontSize: 12.5, lineHeight: 1.6, color: B.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {noteBody?.text || 'This note has no readable text.'}
                    </div>
                  )}
                </div>
              )}

              <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {g.items.map(it => {
                  const key  = itemKey(g, it);
                  const busy = addingAll;
                  const dupe = duplicates[key];
                  return (
                    <div key={key} style={{ border: `1px solid ${dupe ? '#e0b34a' : B.border}`, borderRadius: 6, padding: '8px 10px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{it.title}</div>
                      {it.sourceExcerpt && (
                        <div style={{ fontSize: 11, color: B.textLt, fontStyle: 'italic', margin: '5px 0 0', borderLeft: `2px solid ${B.border}`, paddingLeft: 6 }}>
                          “{it.sourceExcerpt}”
                        </div>
                      )}
                      {dupe && (
                        <div style={{ fontSize: 11, color: '#8a6100', background: '#fdf6e3', border: '1px solid #e8d9a8', borderRadius: 5, padding: '5px 7px', marginTop: 6 }}>
                          Looks like a task you already have: <strong>{dupe[0]?.title}</strong>
                          {dupe.length > 1 ? ` (+${dupe.length - 1} more)` : ''}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                        {it.priority && <span style={{ fontSize: 10, fontWeight: 700, color: B.textMd, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 10, padding: '1px 8px' }}>{it.priority}</span>}
                        {it.dueDate && <span style={{ fontSize: 11, color: B.textMd }}>Due {String(it.dueDate).slice(0, 10)}</span>}
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                          <Btn variant="ghost" size="sm" disabled={busy} onClick={() => onDismiss(g, it)}>Dismiss</Btn>
                          {allowTasks && (
                            <Btn size="sm" disabled={busy} onClick={() => (dupe ? onAddAnyway(g, it) : onAdd(g, it))}>
                              {dupe ? 'Add anyway…' : 'Add task…'}
                            </Btn>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {truncated && (
        <div style={{ fontSize: 11, color: B.textLt, marginTop: 10 }}>
          Showing the most recent notes — work through these and the rest will follow.
        </div>
      )}
      {!allowTasks && (
        <div style={{ fontSize: 11, color: B.textLt, marginTop: 10 }}>Only admins can create tasks — these are for reference.</div>
      )}

      <ModalFooter>
        <Btn variant="ghost" onClick={onDismissAll} disabled={addingAll}>Dismiss all</Btn>
        <Btn variant="ghost" onClick={onClose} disabled={addingAll}>Later</Btn>
        {allowTasks && (
          <Btn disabled={addingAll || total === 0} onClick={onAddAll}>
            {addingAll ? 'Adding…' : `Add all (${total})`}
          </Btn>
        )}
      </ModalFooter>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// The globally-mounted owner: polls for what is outstanding, decides when to
// interrupt, and keeps the server in step with what the reader does.
// ---------------------------------------------------------------------------
export const GlobalTaskProposals = () => {
  const { toast } = useToast();
  const [groups, setGroups]     = useState([]);
  const [meta, setMeta]         = useState({ allowTasks: true, truncated: false });
  const [open, setOpen]         = useState(false);
  const [addingAll, setAddingAll] = useState(false);
  const [duplicates, setDuplicates] = useState({});
  const [expanded, setExpanded] = useState(null);
  // The proposal being edited before it is saved — `{ g, it }`, or null.
  const [editing, setEditing]   = useState(null);
  const [noteBody, setNoteBody] = useState(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const seenRef  = useRef(readSeen());
  // One idempotency key per proposal, held for the life of this list: a retried
  // add (a timeout, an "Add anyway" after the duplicate gate) dedupes against
  // the first attempt instead of creating a second task. Must be a UUID —
  // normalizeRequestId discards anything else server-side.
  const reqIds   = useRef({});
  const mounted  = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async ({ autoOpen = true } = {}) => {
    let res;
    try {
      res = await api.get('note-task-proposals?scope=all');
    } catch { return; }               // best-effort: a failed poll is silent
    if (!mounted.current || !res?.available) return;
    const next = res.proposals || [];
    setGroups(next);
    setMeta({ allowTasks: res.allowTasks !== false, truncated: !!res.truncated });
    if (!autoOpen) return;
    // INTERRUPT ONLY FOR SOMETHING NEW. Everything outstanding is listed once
    // the modal is up — but what OPENS it is a proposal the reader has not been
    // shown before, so an ignored pile does not re-interrupt on every poll.
    const unseen = next.some(g => g.items.some(it => !seenRef.current.has(itemKey(g, it))));
    if (unseen) setOpen(true);
  }, []);

  useEffect(() => {
    const first = setTimeout(() => load(), FIRST_CHECK_MS);
    const t = setInterval(() => load(), POLL_MS);
    return () => { clearTimeout(first); clearInterval(t); };
  }, [load]);

  // The header's Tasks button shows how many proposals are waiting and opens
  // this modal. Events rather than lifted state, because this component is
  // mounted once at the app root and owns the poll.
  useEffect(() => {
    const count = groups.reduce((n, g) => n + g.items.length, 0);
    window.dispatchEvent(new CustomEvent('harvus-task-proposals-count', { detail: { count } }));
  }, [groups]);
  useEffect(() => {
    const onOpen = () => { load({ autoOpen: false }); setOpen(true); };
    window.addEventListener('harvus-open-task-proposals', onOpen);
    return () => window.removeEventListener('harvus-open-task-proposals', onOpen);
  }, [load]);

  const markSeen = (list) => {
    for (const g of list) for (const it of g.items) seenRef.current.add(itemKey(g, it));
    writeSeen(seenRef.current);
  };

  const close = () => { markSeen(groups); setOpen(false); setExpanded(null); };

  // Drop a resolved item locally. The updater stays PURE — closing when the last
  // one goes is an effect, not a setState reached from inside another one.
  const removeItem = (g, it) => {
    seenRef.current.add(itemKey(g, it));
    writeSeen(seenRef.current);
    setGroups(prev => prev
      .map(x => (x.suggestionId === g.suggestionId ? { ...x, items: x.items.filter(y => y.index !== it.index) } : x))
      .filter(x => x.items.length > 0));
  };

  // Nothing left to decide — close, so a later poll returning proposals this
  // reader has already been shown does not re-open on a stale `open`.
  useEffect(() => {
    if (open && groups.length === 0) { setOpen(false); setExpanded(null); }
  }, [open, groups.length]);

  const reqIdFor = (key) => {
    if (!reqIds.current[key]) {
      reqIds.current[key] = window.crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    return reqIds.current[key];
  };

  const record = (g, it, action, taskId) =>
    api.post(`note-task-proposals/${g.suggestionId}/resolve`, { index: it.index, action, taskId: taskId || null })
      .catch(() => { /* bookkeeping only — the item is already gone from the list */ });

  const toggleNote = async (g) => {
    if (expanded === g.suggestionId) { setExpanded(null); return; }
    setExpanded(g.suggestionId);
    setNoteBody(null);
    setNoteLoading(true);
    try {
      const body = await api.get(`note-task-proposals/${g.suggestionId}/note`);
      if (mounted.current) setNoteBody(body);
    } catch (e) {
      if (mounted.current) toast.error(e.message || 'Could not load the note.');
    } finally {
      if (mounted.current) setNoteLoading(false);
    }
  };

  // One create, exactly as drafted — the "Add all" path. It never sets
  // confirmDuplicate: a proposal the server flags stays in the list for its own
  // decision, and the ONLY way past the gate is the editor's "Save anyway",
  // taken after the reader has seen what it resembles.
  const createOne = async (g, it) => {
    const created = await api.post('tasks', {
      title: it.title,
      description: it.description || null,
      priority: it.priority || 'Medium',
      status: 'Not Started',
      dueDate: it.dueDate || null,
      assigneeIds: it.assigneeIds || [],
      // The task lands where the NOTE is — this modal has no page scope of its own.
      dealId: g.dealId,
      dealDetailId: g.dealDetailId || null,
      sourceNoteId: g.noteId,
      clientRequestId: reqIdFor(itemKey(g, it)),
    });
    await record(g, it, 'accepted', created?.taskID || created?.TaskID || null);
    removeItem(g, it);
    notifyTasksChanged();
  };

  // ADD OPENS THE DRAFT; IT DOES NOT CREATE. The model gets a title's wording,
  // an assignee or a due date wrong often enough that the fix belongs before the
  // row exists — so the reader edits it, and Save is the first moment a task is
  // real. "Add anyway" opens the same editor, which shows the duplicate the
  // server named beside the fields that would change it.
  const add = (g, it) => setEditing({ g, it });

  const savedFromEditor = (g, it, created) => {
    setEditing(null);
    setDuplicates(d => { const next = { ...d }; delete next[itemKey(g, it)]; return next; });
    removeItem(g, it);
    void record(g, it, 'accepted', created?.taskID || created?.TaskID || null);
    toast.success('Task created.');
    notifyTasksChanged();
  };

  const dismiss = (g, it) => { record(g, it, 'dismissed'); removeItem(g, it); };

  // ADD ALL DOES NOT OVERRIDE THE DUPLICATE GATE. Anything the server flags as
  // looking like an existing task stays in the list, flagged, for its own
  // decision — one button press must not quietly create a second copy of work
  // somebody has already written down.
  const addAll = async () => {
    setAddingAll(true);
    let added = 0; const dupes = {}; let failed = 0;
    for (const g of groups) {
      for (const it of g.items) {
        if (duplicates[itemKey(g, it)]) continue;      // already flagged — leave it
        try { await createOne(g, it); added++; }
        catch (e) {
          if (e.status === 409 && e.body?.similarTasks?.length) dupes[itemKey(g, it)] = e.body.similarTasks;
          else failed++;
        }
      }
    }
    if (mounted.current) {
      setDuplicates(d => ({ ...d, ...dupes }));
      setAddingAll(false);
      const dupeCount = Object.keys(dupes).length;
      const parts = [`${added} task${added === 1 ? '' : 's'} created`];
      if (dupeCount) parts.push(`${dupeCount} look${dupeCount === 1 ? 's' : ''} like something you already have`);
      if (failed)    parts.push(`${failed} failed`);
      toast[failed ? 'error' : 'success'](parts.join(' · ') + '.');
    }
  };

  const dismissAll = async () => {
    const snapshot = groups;
    setGroups([]); setOpen(false); setExpanded(null);
    markSeen(snapshot);
    // One call per note clears every open proposal on it (no index = all).
    await Promise.allSettled(snapshot.map(g =>
      api.post(`note-task-proposals/${g.suggestionId}/resolve`, { action: 'dismissed' })));
  };

  if (!open || !groups.length) return null;

  // The editor takes over the screen while it is open: one decision at a time,
  // and the list it came from is still there behind Cancel.
  if (editing) {
    const { g, it } = editing;
    return (
      <TaskDraftModal
        draft={{
          title: it.title,
          description: it.description,
          dueDate: it.dueDate,
          priority: it.priority,
          assigneeIds: it.assigneeIds,
          // The task lands where the NOTE is — this pop-up has no page scope.
          dealId: g.dealId,
          dealDetailId: g.dealDetailId,
          dealName: g.dealName,
          sourceNoteId: g.noteId,
          sourceExcerpt: it.sourceExcerpt,
          clientRequestId: reqIdFor(itemKey(g, it)),
        }}
        onCancel={() => setEditing(null)}
        onSaved={(created) => savedFromEditor(g, it, created)}
      />
    );
  }

  return (
    <TaskProposalsModal
      groups={groups}
      allowTasks={meta.allowTasks}
      truncated={meta.truncated}
      addingAll={addingAll}
      duplicates={duplicates}
      expanded={expanded}
      noteBody={noteBody}
      noteLoading={noteLoading}
      onToggleNote={toggleNote}
      onAdd={(g, it) => add(g, it)}
      onAddAnyway={(g, it) => add(g, it)}
      onDismiss={dismiss}
      onAddAll={addAll}
      onDismissAll={dismissAll}
      onClose={close}
    />
  );
};
